import React from 'react';

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ExternalNoteAdditionalFieldConfig } from '@/services/types/external-note-types';

interface DynamicAdditionalFieldProps {
  config: ExternalNoteAdditionalFieldConfig;
  value: string | number | boolean | string[] | null;
  onChange: (value: string | number | boolean | string[] | null) => void;
}

export const DynamicAdditionalField: React.FC<DynamicAdditionalFieldProps> = ({
  config,
  value,
  onChange,
}) => {
  const renderField = () => {
    switch (config.field_type) {
      case 'TEXT': {
        if (config.options && config.options.length > 0) {
          return (
            <Select
              value={(value as string) ?? ''}
              onValueChange={(v) => onChange(v)}
            >
              <SelectTrigger>
                <SelectValue placeholder={`Select ${config.label}...`} />
              </SelectTrigger>
              <SelectContent>
                {config.options.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        return (
          <Input
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={config.label}
          />
        );
      }

      case 'NUMBER': {
        if (config.options && config.options.length > 0) {
          return (
            <Select
              value={value != null ? String(value) : ''}
              onValueChange={(v) => onChange(Number(v))}
            >
              <SelectTrigger>
                <SelectValue placeholder={`Select ${config.label}...`} />
              </SelectTrigger>
              <SelectContent>
                {config.options.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }
        return (
          <Input
            type="number"
            value={value != null ? String(value) : ''}
            onChange={(e) =>
              onChange(e.target.value ? Number(e.target.value) : null)
            }
            placeholder={config.label}
          />
        );
      }

      case 'BOOLEAN':
        return (
          // eslint-disable-next-line jsx-a11y/label-has-associated-control
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-300"
            />
            {config.label}
          </label>
        );

      case 'DATE':
        return (
          <Input
            type="date"
            value={(value as string) ?? ''}
            onChange={(e) => onChange(e.target.value || null)}
          />
        );

      case 'MULTI_SELECT': {
        return (
          <Select
            value={(value as string) ?? ''}
            onValueChange={(v) => onChange(v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Select ${config.label}...`} />
            </SelectTrigger>
            <SelectContent>
              {config.options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }

      default:
        return null;
    }
  };

  const fieldId = `push-cms-field-${config.external_key}`;

  return (
    <div className="space-y-1.5">
      {config.field_type !== 'BOOLEAN' && (
        <label
          htmlFor={fieldId}
          className="text-xs font-medium uppercase tracking-wide text-zinc-500"
        >
          {config.label}
          {config.required && <span className="ml-0.5 text-red-500">*</span>}
        </label>
      )}
      {renderField()}
    </div>
  );
};
